import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { HopeLogo } from "@/shared/branding/TheHopeOnelogo";
import { HOPE_SANS, HOPE_SERIF } from "@/shared/branding/thehopeonebrand";

const reveal = "hopeLoaderReveal 0.38s ease-out forwards";

export function ScreenLoader() {
  const { t } = useTranslation("common");

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        bgcolor: "var(--page-bg)",
        color: "var(--text)",
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          px: 2,
          py: 4,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 380,
            borderRadius: "24px",
            bgcolor: "var(--page-bg)",
            animation: `${reveal}`,
            opacity: 0,
            "@keyframes hopeLoaderReveal": {
              from: { opacity: 0, transform: "translateY(12px) scale(0.98)" },
              to: { opacity: 1, transform: "translateY(0) scale(1)" },
            },
            "@media (prefers-reduced-motion: reduce)": {
              animation: "none",
              opacity: 1,
            },
          }}
        >
          <Box
            sx={{
              borderRadius: "24px",
              px: { xs: 3.5, sm: 4.5 },
              py: { xs: 4, sm: 4.5 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: { xs: 2.25, sm: 2.75 },
            }}
          >
            <Box
              sx={{
                animation: `${reveal}`,
                animationDelay: "0.06s",
                opacity: 0,
                "@media (prefers-reduced-motion: reduce)": { animation: "none", opacity: 1 },
              }}
            >
              <HopeLogo size="lg" />
            </Box>

            <Typography
              component="span"
              sx={{
                display: "inline-block",
                fontFamily: HOPE_SERIF,
                fontSize: { xs: "1rem", sm: "1.1rem" },
                fontWeight: 500,
                letterSpacing: { xs: 0.4, sm: 0.8 },
                textAlign: "center",
                lineHeight: 1.45,
                maxWidth: 280,
                color: "var(--text)",
                animation: `${reveal}`,
                animationDelay: "0.1s",
                opacity: 0,
                "@media (prefers-reduced-motion: reduce)": { animation: "none", opacity: 1 },
              }}
            >
              {t("every_card_a_cause")}
            </Typography>

            <Box
              aria-hidden
              sx={{
                width: 48,
                height: 1,
                bgcolor: "var(--text)",
                opacity: 0.28,
                animation: `${reveal}`,
                animationDelay: "0.14s",
                "@media (prefers-reduced-motion: reduce)": { animation: "none", opacity: 1 },
              }}
            />

            <Box
              sx={{
                position: "relative",
                width: { xs: 140, sm: 168 },
                height: 3,
                borderRadius: 999,
                bgcolor: "color-mix(in srgb, var(--text) 14%, transparent)",
                overflow: "hidden",
                animation: `${reveal}`,
                animationDelay: "0.18s",
                opacity: 0,
                "@media (prefers-reduced-motion: reduce)": { animation: "none", opacity: 1 },
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: "0 0 0 0",
                  width: "42%",
                  borderRadius: 999,
                  bgcolor: "var(--text)",
                  animation: "hopeLoaderShimmer 1.1s ease-in-out infinite",
                  "@keyframes hopeLoaderShimmer": {
                    "0%": { transform: "translateX(-110%)" },
                    "100%": { transform: "translateX(320%)" },
                  },
                  "@media (prefers-reduced-motion: reduce)": {
                    animation: "none",
                    width: "55%",
                    left: "22.5%",
                  },
                }}
              />
            </Box>

            <Typography
              sx={{
                fontFamily: HOPE_SANS,
                fontSize: "0.68rem",
                fontWeight: 600,
                letterSpacing: 2.8,
                textTransform: "uppercase",
                color: "var(--text)",
                opacity: 0.72,
                animation: `${reveal}`,
                animationDelay: "0.28s",
                "@media (prefers-reduced-motion: reduce)": { animation: "none", opacity: 1 },
              }}
            >
              {t("loading")}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
